import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/apiClient";
import { GraduationCap, CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const done = useRef(false);
  const [state, setState] = useState("loading"); // loading | ok | error
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const token = params.get("token");
    if (!token) {
      setState("error");
      setMsg("Missing verification token.");
      return;
    }
    api.post("/auth/verify-email", { token })
      .then(() => setState("ok"))
      .catch((e) => { setState("error"); setMsg(apiErr(e)); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="h-10 w-10 rounded-lg bg-[#6366F1] flex items-center justify-center">
            <GraduationCap size={22} className="text-white" />
          </div>
          <span className="font-extrabold tracking-tight text-xl text-white">Enjyme</span>
        </Link>
        <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-xl text-center" data-testid="verify-email-card">
          {state === "loading" && (
            <div className="flex flex-col items-center gap-3 py-4">
              <Loader2 className="animate-spin text-[#6366F1]" size={28} />
              <p className="text-slate-500 text-sm">Verifying your email…</p>
            </div>
          )}
          {state === "ok" && (
            <div className="flex flex-col items-center gap-3 py-2" data-testid="verify-ok">
              <CheckCircle2 className="text-[#22C55E]" size={44} />
              <h1 className="font-display font-extrabold text-2xl tracking-tight text-slate-900">Email verified</h1>
              <p className="text-sm text-slate-500">Your account is now active. Please sign in to continue.</p>
              <Link to="/login" data-testid="verify-to-login"
                className="mt-2 w-full bg-[#6366F1] hover:bg-[#4F46E5] active:scale-[0.98] text-white font-semibold py-2.5 rounded-lg transition-colors">
                Go to sign in
              </Link>
            </div>
          )}
          {state === "error" && (
            <div className="flex flex-col items-center gap-3 py-2" data-testid="verify-error">
              <XCircle className="text-red-500" size={44} />
              <h1 className="font-display font-extrabold text-2xl tracking-tight text-slate-900">Verification failed</h1>
              <p className="text-sm text-slate-500">{msg}</p>
              <Link to="/login" className="mt-2 text-[#6366F1] font-semibold hover:underline">
                Back to sign in to resend
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
